import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateDashboardRequest,
  type DashboardDto,
  type DashboardListResponse,
  DashboardListResponseSchema,
  type DashboardMode,
  DashboardSchema,
  type UpdateDashboardRequest,
} from '@vision360/contracts';
import { useApi } from '@/lib/auth/session-context';

/** Dashboards over the API (TechSpec §6.1, §9.5). */

export const dashboardsKey = (mode: DashboardMode) => ['dashboards', mode] as const;

export function useDashboards(mode: DashboardMode = 'web') {
  const api = useApi();
  return useQuery({
    queryKey: dashboardsKey(mode),
    queryFn: ({ signal }) =>
      api.request(`/api/v1/dashboards?mode=${mode}`, {
        schema: DashboardListResponseSchema,
        signal,
      }),
  });
}

/** Writes the server's answer into the cached list, so every view agrees. */
function useStore(mode: DashboardMode) {
  const queryClient = useQueryClient();
  return (update: (list: DashboardDto[]) => DashboardDto[]) =>
    queryClient.setQueryData<DashboardListResponse>(dashboardsKey(mode), (current) =>
      current ? { dashboards: update(current.dashboards) } : current,
    );
}

const byName = (a: DashboardDto, b: DashboardDto) => a.name.localeCompare(b.name, 'fr');

export function useCreateDashboard(mode: DashboardMode = 'web') {
  const api = useApi();
  const store = useStore(mode);
  return useMutation({
    mutationFn: (body: CreateDashboardRequest) =>
      api.request('/api/v1/dashboards', { method: 'POST', body, schema: DashboardSchema }),
    onSuccess: (created) =>
      store((list) =>
        [...list.map((d) => (created.isDefault ? { ...d, isDefault: false } : d)), created].sort(
          byName,
        ),
      ),
  });
}

export function useUpdateDashboard(mode: DashboardMode = 'web') {
  const api = useApi();
  const store = useStore(mode);
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateDashboardRequest }) =>
      api.request(`/api/v1/dashboards/${id}`, {
        method: 'PUT',
        body: patch,
        schema: DashboardSchema,
      }),
    onSuccess: (saved) =>
      store((list) =>
        list
          .map((d) =>
            d.id === saved.id ? saved : saved.isDefault ? { ...d, isDefault: false } : d,
          )
          .sort(byName),
      ),
  });
}

export function useDeleteDashboard(mode: DashboardMode = 'web') {
  const api = useApi();
  const store = useStore(mode);
  return useMutation({
    mutationFn: (id: string) => api.request(`/api/v1/dashboards/${id}`, { method: 'DELETE' }),
    onSuccess: (_, id) => store((list) => list.filter((d) => d.id !== id)),
  });
}
